import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ActiveTasks from '@/assets/icons/operational-dashboard/active-tasks.svg?component';
import ActivityLogIcon from '@/assets/icons/operational-dashboard/activity-log.svg?component';
import IgnoredTasks from '@/assets/icons/operational-dashboard/ignored-tasks.svg?component';
import TasksCompleted from '@/assets/icons/operational-dashboard/tasks-completed.svg?component';
import AddLine from '@/assets/icons/ri_add_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import EmptyState from '@/components/molecules/EmptyState';
import Tabs from '@/components/molecules/Tabs';
import type { ActivityLog, Task, TaskList } from '@/domain/operationalDashboard';

import ActivityLogEntry from '../ActivityLogEntry/ActivityLogEntry';
import ActivityLogEntrySkeleton from '../ActivityLogEntry/ActivityLogEntrySkeleton';
import TaskCard from '../TaskCard/TaskCard';
import TaskCardSkeleton from '../TaskCard/TaskCardSkeleton';

import styles from './OperationalTasksPanel.module.scss';

type ActiveTab = 'tasks' | 'ignoredTasks' | 'activityLog';

const SKELETON_KEYS = ['skeleton-a', 'skeleton-b', 'skeleton-c'];

interface IgnoreTaskParams {
    taskId: number;
    ignoredUntil?: Date;
    ignoredPermanently?: boolean;
}

interface OperationalTasksPanelProps {
    taskList?: TaskList;
    activityLog?: ActivityLog;
    tasksLoading: boolean;
    activityLoading: boolean;
    onCompleteTask: (taskId: number) => void;
    onIgnoreTask: (params: IgnoreTaskParams) => void;
    onUnignoreTask: (taskId: number) => void;
    onAddActivityClick: () => void;
}

export default function OperationalTasksPanel(props: OperationalTasksPanelProps) {
    const {
        taskList,
        activityLog,
        tasksLoading,
        activityLoading,
        onCompleteTask,
        onIgnoreTask,
        onUnignoreTask,
        onAddActivityClick,
    } = props;
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<ActiveTab>('tasks');

    const activeTasks = taskList?.tasks.filter((task: Task) => task.status === 'active') ?? [];
    const ignoredTasks = taskList?.tasks.filter((task: Task) => task.status === 'ignored') ?? [];
    const activityEntries = activityLog?.entries ?? [];

    return (
        <ContentBlock>
            <div className={styles.tabRow}>
                <Tabs<ActiveTab>
                    layout="equal"
                    value={activeTab}
                    onChange={(tab) => setActiveTab(tab)}
                    items={[
                        {
                            value: 'tasks',
                            icon: ActiveTasks,
                            iconSize: 'small',
                            label: t('operationalDashboard.tabs.activeTasks'),
                            badgeCount: !tasksLoading ? activeTasks.length : undefined,
                        },
                        {
                            value: 'ignoredTasks',
                            icon: IgnoredTasks,
                            iconSize: 'small',
                            label: t('operationalDashboard.tabs.ignoredTasks'),
                            badgeCount: !tasksLoading ? ignoredTasks.length : undefined,
                        },
                        {
                            value: 'activityLog',
                            icon: ActivityLogIcon,
                            iconSize: 'small',
                            label: t('operationalDashboard.tabs.activityLog'),
                        },
                    ]}
                />
            </div>

            {activeTab === 'tasks' && (
                <div className={styles.taskList}>
                    {tasksLoading ? (
                        SKELETON_KEYS.map((key) => <TaskCardSkeleton key={key} />)
                    ) : activeTasks.length === 0 ? (
                        <EmptyState
                            appearance="prominent"
                            className={styles.emptyState}
                            icon={
                                <SvgIcon
                                    icon={TasksCompleted}
                                    size="custom"
                                    customSize={64}
                                    color="var(--tint-ocean-green-fg)"
                                />
                            }
                            title={t('operationalDashboard.emptyState.title')}
                            description={t('operationalDashboard.emptyState.subtitle')}
                        />
                    ) : (
                        activeTasks.map((task) => (
                            <TaskCard
                                key={task.id}
                                task={task}
                                onComplete={onCompleteTask}
                                onIgnore={onIgnoreTask}
                                onUnignore={onUnignoreTask}
                            />
                        ))
                    )}
                </div>
            )}

            {activeTab === 'ignoredTasks' && (
                <div className={styles.taskList}>
                    {tasksLoading ? (
                        SKELETON_KEYS.map((key) => <TaskCardSkeleton key={key} />)
                    ) : ignoredTasks.length === 0 ? (
                        <EmptyState
                            appearance="quiet"
                            className={styles.emptyState}
                            title={t('operationalDashboard.emptyState.noIgnoredTasks')}
                        />
                    ) : (
                        ignoredTasks.map((task) => (
                            <TaskCard
                                key={task.id}
                                task={task}
                                onComplete={onCompleteTask}
                                onIgnore={onIgnoreTask}
                                onUnignore={onUnignoreTask}
                            />
                        ))
                    )}
                </div>
            )}

            {activeTab === 'activityLog' && (
                <div className={styles.taskList}>
                    {activityLoading ? (
                        SKELETON_KEYS.map((key) => <ActivityLogEntrySkeleton key={key} />)
                    ) : activityEntries.length === 0 ? (
                        <>
                            <div className={styles.tabActions}>
                                <Button
                                    variant="secondary"
                                    trackingId="operational_add_activity"
                                    icon={<SvgIcon icon={AddLine} />}
                                    onClick={onAddActivityClick}>
                                    {t('operationalDashboard.addActivity')}
                                </Button>
                            </div>
                            <EmptyState
                                appearance="quiet"
                                className={styles.emptyState}
                                title={t('operationalDashboard.activityLog.empty')}
                            />
                        </>
                    ) : (
                        <div className={styles.activityLogList}>
                            <div className={styles.tabActions}>
                                <Button
                                    variant="secondary"
                                    trackingId="operational_add_activity"
                                    icon={<SvgIcon icon={AddLine} />}
                                    onClick={onAddActivityClick}>
                                    {t('operationalDashboard.addActivity')}
                                </Button>
                            </div>
                            {activityEntries.map((entry) => (
                                <ActivityLogEntry key={entry.id} entry={entry} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </ContentBlock>
    );
}
